import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import type * as React from 'react';
import { cn } from '@/lib/utils';

export const Sheet = DialogPrimitive.Root;
export const SheetTrigger = DialogPrimitive.Trigger;
export const SheetClose = DialogPrimitive.Close;

/**
 * A panel that slides in from the edge, for inspecting one row without losing
 * the list behind it.
 *
 * It is the same Radix dialog the modal uses — focus trap, Escape, restored
 * focus, `aria-modal` — anchored to the right instead of centred. Sharing the
 * primitive matters: a hand-built side panel is the one that forgets to return
 * focus to the row that opened it.
 *
 * On a phone it becomes full width, because a 28rem drawer on a 22rem screen
 * is just a modal that clips.
 */
/** Which edge it comes from. A drawer should arrive from the button that opened it. */
const SIDES = {
  right: [
    'inset-y-0 right-0 max-w-md',
    'data-[state=open]:slide-in-from-right data-[state=closed]:slide-out-to-right',
  ].join(' '),
  left: [
    'inset-y-0 left-0 max-w-xs',
    'data-[state=open]:slide-in-from-left data-[state=closed]:slide-out-to-left',
  ].join(' '),
} as const;

export function SheetContent({
  className,
  children,
  title,
  description,
  side = 'right',
  ...props
}: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
  title: string;
  description?: React.ReactNode;
  side?: keyof typeof SIDES;
}) {
  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-ink/25 backdrop-blur-[2px] data-[state=open]:animate-in data-[state=open]:fade-in-0" />
      <DialogPrimitive.Content
        className={cn(
          'fixed z-50 flex w-full flex-col bg-surface shadow-xl',
          'data-[state=open]:animate-in data-[state=closed]:animate-out',
          SIDES[side],
          className,
        )}
        {...props}
      >
        <div className="flex items-start justify-between gap-4 border-b border-line px-5 py-4">
          <div className="min-w-0 space-y-0.5">
            <DialogPrimitive.Title className="truncate text-base font-bold tracking-tight text-ink">
              {title}
            </DialogPrimitive.Title>
            {description && (
              <DialogPrimitive.Description asChild>
                <div className="text-sm text-muted">{description}</div>
              </DialogPrimitive.Description>
            )}
          </div>
          <DialogPrimitive.Close
            className="grid size-8 shrink-0 place-items-center rounded-lg text-muted hover:bg-surface-2 hover:text-ink"
            aria-label="Close"
          >
            <X className="size-4" />
          </DialogPrimitive.Close>
        </div>
        {children}
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
}

export function SheetBody({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('flex-1 space-y-5 overflow-y-auto px-5 py-5', className)} {...props} />;
}

export function SheetFooter({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn('flex flex-wrap justify-end gap-2 border-t border-line px-5 py-4', className)} {...props} />
  );
}
